import moment from 'moment'
import React, { MouseEvent } from 'react'
import { Card, EmployeeSelect, Flex, Toaster, IGridTableColumn, NewGridTable } from '@ui'
import StatusSelect, { getTicketStatus } from './StatusSelect'
import PrioritySelect from './PrioritySelect'
import Toolbar from './Toolbar'
import EmployeeWithDetailsPopover from '@ui/EmployeeWithDetailsPopover'
import TicketNumber from './TicketNumber'
import track from '@track'
import Event from '@track/Event'
import { get } from 'lodash'
import api, { ITicket, TicketStatus, ITicketDetails } from '@api'
import { SERVER_DATETIME_FMT } from '@constants'
import { NavigateFn } from '@reach/router'
import { UseTicketsHook } from './hooks/useTickets'
import classnames from 'classnames'
import { Classes } from '@blueprintjs/core'

interface TableViewProps {
  navigate: NavigateFn
  tickets: UseTicketsHook
  location: Location
}

const placeholder = (placeholder = 'placeholder') => (
  <div
    className={classnames({
      [Classes.SKELETON]: true,
    })}
  >
    {placeholder}
  </div>
)

export default function TableView({ navigate, tickets }: TableViewProps) {
  const { selection, items, sort, sortBy, pageInfo, nextPage } = tickets

  const columns: IGridTableColumn<ITicket>[] = [
    {
      label: 'Ticket',
      sort: 'ticketNumber',
      Cell: ({ skeleton, cell }) => {
        return (
          <TicketItem
            ticket={cell?.data}
            skeleton={skeleton}
            onShow={() => {
              track.tickets({ eventName: Event.ROW_CLICK })
              if (cell) navigate(`?ticketId=${cell.data.id}`)
            }}
          />
        )
      },
    },
    {
      label: 'Created By',
      Cell: ({ cell, skeleton }) => {
        if (skeleton)
          return (
            <span
              className={classnames({
                [Classes.SKELETON]: skeleton,
              })}
            >
              Created by Placeholder
            </span>
          )

        return cell?.data.employee ? (
          <EmployeeWithDetailsPopover
            buttonProps={{ minimal: true, small: true }}
            employee={cell.data.employee}
          />
        ) : (
          <span>-</span>
        )
      },
    },
    {
      label: 'Created at',
      sort: 'createdDate',
      Cell: ({ cell, skeleton }) => (
        <span
          data-test="ticket-created-at"
          className={classnames({
            [Classes.SKELETON]: skeleton,
          })}
        >
          {cell?.data.createdDate ? (
            moment.utc(cell?.data.createdDate, SERVER_DATETIME_FMT).fromNow()
          ) : (
            <span>{skeleton ? 'Placeholder' : '-'}</span>
          )}
        </span>
      ),
    },
    {
      label: 'Priority',
      sort: 'severity',
      Cell: ({ cell, skeleton }) => {
        if (skeleton || !cell?.data) return placeholder()

        return (
          <PrioritySelect
            value={cell.data.severity}
            onChange={(severity) => {
              const updatedTicket = { ...cell.data, severity: severity }
              tickets.update(updatedTicket)
              api.ticket
                .update(updatedTicket as ITicketDetails)
                .then(() => Toaster.success({ message: 'Priority updated' }))
                .then(() => tickets.reload())
                .catch((error) => Toaster.fail({ error }))
              track.tickets({
                eventName: Event.INLINE_ACTION,
                props: {
                  actionName: 'Severity',
                  actionValue: severity,
                },
              })
            }}
            buttonProps={{
              ghost: true,
              'data-button': 'priority_select',
            }}
          />
        )
      },
    },
    {
      label: 'Status',
      sort: 'status',
      Cell: ({ cell, skeleton }) => {
        if (skeleton || !cell?.data) return placeholder()

        return (
          <StatusSelect
            value={getTicketStatus(cell.data.status)}
            onChange={(status) => {
              const updatedTicket = {
                ...cell.data,
                status: status ? status.value : TicketStatus.NEW,
              }
              tickets.update(updatedTicket)
              api.ticket
                .update(updatedTicket as ITicketDetails)
                .then(() => Toaster.success({ message: 'Status updated' }))
                .then(() => tickets.reload())
                .catch((error) => Toaster.fail({ error }))
              track.tickets({
                eventName: Event.INLINE_ACTION,
                props: {
                  actionName: 'Status',
                  actionValue: status ? status.label : 'New',
                },
              })
            }}
            buttonProps={{
              ghost: true,
              'data-button': 'status_select',
            }}
          />
        )
      },
    },
    {
      label: 'Assigned To',
      Cell: ({ cell, skeleton }) => {
        if (skeleton || !cell?.data) return placeholder()

        return (
          <EmployeeSelect
            clearable
            value={cell.data.assignedTo}
            onChange={(employee) => {
              const updatedTicket = { ...cell.data, assignedTo: employee }
              tickets.update(updatedTicket)
              api.ticket
                .update(updatedTicket as ITicketDetails)
                .then(() => Toaster.success({ message: 'Assigned to field updated' }))
                .then(() => tickets.reload())
                .catch((error) => Toaster.fail({ error }))
              track.tickets({
                eventName: Event.INLINE_ACTION,
                props: {
                  actionName: 'Assigned to',
                  actionValue: get(employee, 'name', null),
                },
              })
            }}
            buttonProps={{
              ghost: true,
              'data-button': 'assigned-to_select',
            }}
          />
        )
      },
    },
    {
      label: 'Due date',
      sort: 'dueDate',
      Cell: ({ cell, skeleton }) => {
        if (skeleton || !cell?.data) return placeholder('MMM DD')

        return (
          <div data-test="ticket-due-date">
            {cell.data.dueDate
              ? moment(cell.data.dueDate, SERVER_DATETIME_FMT).format('MMM DD')
              : 'N/A'}
          </div>
        )
      },
    },
  ]

  return (
    <>
      <Toolbar {...{ tickets, selection, navigate }} />
      <NewGridTable<ITicket>
        status={tickets.status}
        columns={columns}
        data={items}
        selection={selection}
        minCellWidth="max-content"
        sort={{
          ...sort,
          onSort: (column) => {
            sortBy(column)
            track.tickets({
              eventName: Event.SORT,
              props: { columnName: column },
            })
          },
        }}
        page={{
          hasNextPage: pageInfo.hasNextPage,
          endCursor: pageInfo.endCursor,
          onNextPage: () => {
            nextPage()
            track.tickets({
              eventName: Event.PAGE,
            })
          },
        }}
        skeletonSize={3}
        track={{
          select: () =>
            track.tickets({
              eventName: Event.ROW_SELECT,
            }),
          selectAll: () =>
            track.tickets({
              eventName: Event.SELECT_ALL,
            }),
        }}
        headerTop={52}
      />
    </>
  )
}

function TicketItem({
  ticket,
  skeleton,
  onShow,
}: {
  ticket?: ITicket
  onShow: (e: MouseEvent) => void
  skeleton?: boolean
}) {
  return (
    <div
      className={classnames('w-full', 'relative', {
        [Classes.SKELETON]: skeleton,
      })}
    >
      <div onClick={onShow} className="table__link">
        {ticket
          ? ticket.shortDescription || ticket.ticketNumber || ticket.id
          : 'Ticket Placeholder'}
      </div>
      <Flex align="center" wrap="wrap" style={{ paddingTop: 2 }}>
        {ticket && <TicketNumber ticket={ticket} />}
      </Flex>
    </div>
  )
}
